"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  AgeResults,
  PercentResults,
  type RadiocarbonResultStrings,
} from "@/components/tools/chem-reference/radiocarbon-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import {
  describeFraction,
  fractionFromConventionalAge,
  fractionFromInput,
  type InputMode,
  type RadiocarbonError,
} from "@/lib/tool-kit/chem-reference/radiocarbon";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const RADIOCARBON_DATING_CALCULATOR_STRINGS = {
  modeLabel: "Start from",
  modePmc: "Percent modern carbon (pMC)",
  modeF14c: "Fraction modern (F¹⁴C)",
  modeRatio: "¹⁴C/¹²C ratio",
  modeAge: "A conventional age (find pMC)",
  valueLabels: {
    pmc: "Percent modern carbon (pMC)",
    f14c: "Fraction modern (F¹⁴C)",
    ratio: "Sample ¹⁴C/¹²C ratio",
    age: "Conventional radiocarbon age (¹⁴C yr BP)",
  },
  valueHints: {
    pmc: "100 pMC is the modern standard; 50 pMC is one Libby half-life old.",
    f14c: "Sample activity divided by the modern standard: 1 is modern, 0.5 is half.",
    ratio:
      "Use the same units as the reference ratio. Scientific input such as 3.6e-13 works.",
    age: "Years before 1950 on the Libby half-life of 5,568 y.",
  },
  sigmaLabel: "Uncertainty, ±1σ (optional)",
  sigmaHint: "In the same unit as the value above.",
  referenceLabel: "Modern reference ¹⁴C/¹²C ratio",
  referenceHint:
    "Atmospheric carbon is about 1.2 × 10⁻¹²; use the standard your measurement was made against.",
  deltaLabel: "δ¹³C in ‰ (optional)",
  deltaHint:
    "Normalises to −25 ‰. Leave blank if your lab already reported a normalised value, as most do.",
  tryLabel: "Try an example",
  results: "Results",
  enterPrompt: "Enter a value to see the age.",
  invalid:
    "“{field}” is not a number. Use digits, a decimal point, or 4.5e-13.",
  errors: {
    value: "The value must be greater than zero.",
    reference: "Enter a reference ratio greater than zero.",
    sigma: "The uncertainty cannot be negative.",
    fractionation: "δ¹³C should lie between −100 ‰ and +100 ‰.",
    age: "The age must be zero or more.",
  },
  libbyAge: "Conventional age (Libby half-life {half} y)",
  cambridgeAge: "Age on the Cambridge half-life ({half} y)",
  fractionModern: "Fraction modern (F¹⁴C)",
  percentModern: "Percent modern carbon (pMC)",
  yearsBp: "¹⁴C yr BP",
  ageWithSigma: "{age} ± {sigma}",
  modernNote:
    "F¹⁴C is 1 or more, so the sample is modern or contains post-1950 bomb carbon. It has no radiocarbon age; report it as F¹⁴C.",
  limitNote:
    "This is beyond the usual limit of about 50,000 years (roughly 0.2 pMC). At this level a trace of modern contamination or the instrument background can dominate, so treat it as a minimum age.",
  calibrationNote:
    "This is an uncalibrated radiocarbon age. It is not a calendar date: atmospheric ¹⁴C has varied, so a calibration curve (IntCal20 for the northern hemisphere) and a program such as OxCal or CALIB are needed to turn it into calendar years.",
  working: "The working",
  workFractionation:
    "δ¹³C normalisation: A_SN = A_S × [1 − 2(25 + δ¹³C) ÷ 1000], factor {factor}",
  workFraction: "F¹⁴C = {f}, so pMC = {pmc}",
  workLibby: "Libby: t = −{life} × ln F¹⁴C = −{life} × ln({f}) = {age} years",
  workCambridge:
    "Cambridge: t = −({half} ÷ ln 2) × ln F¹⁴C = −{life} × ln({f}) = {age} years",
  workSigma:
    "σₜ = {life} × σ(F¹⁴C) ÷ F¹⁴C = {life} × {relative}% = {sigma} years",
  workReverse: "F¹⁴C = e^(−t ÷ {life}) = e^(−{age} ÷ {life}) = {f}",
  tableHeading: "Percent modern and age on each half-life",
  columnPmc: "pMC",
  columnLibby: "Libby age (y)",
  columnCambridge: "Cambridge age (y)",
  copy: "Copy result",
  copied: "Copied!",
  copyAge: "{age} ¹⁴C yr BP (Libby, uncalibrated); {pmc} pMC",
  copyPmc: "{pmc} pMC; F¹⁴C = {f}",
} as const;

type Strings = typeof RADIOCARBON_DATING_CALCULATOR_STRINGS;
type Mode = InputMode | "age";

interface Entry {
  value: string;
  sigma: string;
}

const DEFAULTS: Record<Mode, Entry> = {
  pmc: { value: "30", sigma: "0.3" },
  f14c: { value: "0.3", sigma: "0.003" },
  ratio: { value: "3.6e-13", sigma: "3.6e-15" },
  age: { value: "9672", sigma: "80" },
};

const MODES: readonly Mode[] = ["pmc", "f14c", "ratio", "age"];
const MODE_STRING = {
  pmc: "modePmc",
  f14c: "modeF14c",
  ratio: "modeRatio",
  age: "modeAge",
} as const;

const EXAMPLES: readonly { mode: Mode; value: string; sigma: string }[] = [
  { mode: "pmc", value: "50", sigma: "" },
  { mode: "pmc", value: "25", sigma: "" },
  { mode: "pmc", value: "10", sigma: "" },
  { mode: "pmc", value: "1", sigma: "" },
];

/** Blank → null; unreadable → NaN. */
function read(raw: string): number | null {
  if (raw.trim() === "") {
    return null;
  }
  return parseMeasuredNumber(raw) ?? Number.NaN;
}

function errorText(strings: Strings, error: RadiocarbonError | "age") {
  return strings.errors[error];
}

export default function RadiocarbonDatingCalculatorWidget() {
  const strings = useToolUi(RADIOCARBON_DATING_CALCULATOR_STRINGS);
  const [mode, setMode] = useState<Mode>("pmc");
  const [entries, setEntries] = useState<Record<Mode, Entry>>(DEFAULTS);
  const [reference, setReference] = useState("1.2e-12");
  const [delta, setDelta] = useState("");

  const entry = entries[mode];
  const patch = (next: Partial<Entry>) =>
    setEntries({ ...entries, [mode]: { ...entry, ...next } });

  const value = read(entry.value);
  const sigma = read(entry.sigma);
  const referenceValue = read(reference);
  const deltaValue = read(delta);
  const fields: [string, number | null][] = [
    [strings.valueLabels[mode], value],
    [strings.sigmaLabel, sigma],
    ...(mode === "ratio"
      ? ([[strings.referenceLabel, referenceValue]] as [
          string,
          number | null,
        ][])
      : []),
    ...(mode === "age"
      ? []
      : ([[strings.deltaLabel, deltaValue]] as [string, number | null][])),
  ];
  const unreadable = fields.find(([, v]) => v !== null && Number.isNaN(v));

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="rc-mode"
        onChange={setMode}
        options={MODES.map((m) => ({
          value: m,
          label: strings[MODE_STRING[m]],
        }))}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.valueHints[mode]}
          id="rc-value"
          label={strings.valueLabels[mode]}
          numeric
          onChange={(v) => patch({ value: v })}
          value={entry.value}
        />
        <TextField
          hint={strings.sigmaHint}
          id="rc-sigma"
          label={strings.sigmaLabel}
          numeric
          onChange={(v) => patch({ sigma: v })}
          value={entry.sigma}
        />
        {mode === "ratio" ? (
          <TextField
            hint={strings.referenceHint}
            id="rc-reference"
            label={strings.referenceLabel}
            numeric
            onChange={setReference}
            value={reference}
          />
        ) : null}
        {mode === "age" ? null : (
          <TextField
            hint={strings.deltaHint}
            id="rc-delta"
            label={strings.deltaLabel}
            numeric
            onChange={setDelta}
            placeholder="−25"
            value={delta}
          />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.tryLabel}</span>
        {EXAMPLES.map((example) => (
          <button
            className={BUTTON_SMALL}
            key={example.value}
            onClick={() => {
              setMode(example.mode);
              setEntries({
                ...entries,
                [example.mode]: { value: example.value, sigma: example.sigma },
              });
            }}
            type="button"
          >
            {example.value} pMC
          </button>
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {unreadable ? (
          <ErrorNotice>
            {strings.invalid.replace("{field}", unreadable[0])}
          </ErrorNotice>
        ) : (
          <Outcome
            delta={deltaValue}
            mode={mode}
            reference={referenceValue}
            sigma={sigma}
            strings={strings}
            value={value}
          />
        )}
      </section>
    </>
  );
}

function Outcome({
  mode,
  value,
  sigma,
  reference,
  delta,
  strings,
}: {
  mode: Mode;
  value: number | null;
  sigma: number | null;
  reference: number | null;
  delta: number | null;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  if (value === null) {
    return <Notice>{strings.enterPrompt}</Notice>;
  }
  const resultStrings = strings as RadiocarbonResultStrings;
  if (mode === "age") {
    const forward = fractionFromConventionalAge(value, sigma);
    if ("error" in forward) {
      return <ErrorNotice>{errorText(strings, forward.error)}</ErrorNotice>;
    }
    return (
      <PercentResults
        age={value}
        fmt={fmt}
        result={forward}
        sigma={sigma ?? 0}
        strings={resultStrings}
      />
    );
  }
  const fraction = fractionFromInput({
    mode,
    value,
    sigma,
    reference,
    delta13c: delta,
  });
  if ("error" in fraction) {
    return <ErrorNotice>{errorText(strings, fraction.error)}</ErrorNotice>;
  }
  return (
    <AgeResults
      fmt={fmt}
      fraction={fraction}
      result={describeFraction(fraction)}
      strings={resultStrings}
    />
  );
}
